import React, { useState } from "react";
import { Link } from "react-router-dom";
import "../css/GlobalMontessori.css";
import SchoolHero from "../components/SchoolLevels/SchoolHero";

const SCHOOL = {
  name: "Sahara Public Secondary School",
  city: "Nepal",
  programme: "Montessori Early Years Programme",
  phone: "+977-9847040404",
};

const aboutImage = "./images/a.jpeg";

const philosophyImage = "./images/m.jpeg";

const LINKS = {
  admission: "/admission",
  enquiry: "/contact",
};

const AGE_GROUPS = [
  {
    title: "Play Group",
    age: "2.5 – 3 Years",
    description:
      "A safe, caring and joyful environment that introduces children to classroom routines through games, music and exploration.",
  },
  {
    title: "Nursery",
    age: "3 – 4 Years",
    description:
      "Focus on communication, sensory activities, creativity and confidence through activity-based learning.",
  },
  {
    title: "LKG",
    age: "4 – 5 Years",
    description:
      "Children develop literacy, numeracy, practical life skills and social interaction through Montessori materials.",
  },
  {
    title: "UKG",
    age: "5 – 6 Years",
    description:
      "Preparation for primary education through structured learning, curiosity and independent thinking.",
  },
];

const LEARNING_AREAS = [
  {
    icon: "🌱",
    title: "Practical Life",
    description:
      "Activities that develop independence, concentration and responsibility.",
  },
  {
    icon: "🧩",
    title: "Sensorial",
    description:
      "Hands-on materials helping children refine observation and logical thinking.",
  },
  {
    icon: "📚",
    title: "Language",
    description:
      "Listening, speaking, reading and writing through phonics and storytelling.",
  },
  {
    icon: "➕",
    title: "Mathematics",
    description:
      "Concrete Montessori materials make mathematical concepts easy and enjoyable.",
  },
  {
    icon: "🌍",
    title: "Science & Nature",
    description:
      "Observation, exploration and experiments encourage curiosity about the world.",
  },
  {
    icon: "🎨",
    title: "Arts & Creativity",
    description:
      "Drawing, music, dance and craft inspire imagination and self-expression.",
  },
];

const WHY_CHOOSE = [
  "Certified Montessori Teachers",
  "Interactive Smart Classrooms",
  "Safe & Secure Campus",
  "Activity Based Learning",
  "Indoor & Outdoor Play Zones",
  "Strong Parent Partnership",
];

const FAQS = [
  {
    q: "What is the age for Montessori admission?",
    a: "Children from 2.5 to 6 years can join the Montessori programme according to the admission age criteria.",
  },
  {
    q: "Is Montessori education activity-based?",
    a: "Yes. Children learn through exploration, observation, play and hands-on activities instead of rote memorisation.",
  },
  {
    q: "Is transportation available?",
    a: "Yes. School transport is available on selected routes across the city.",
  },
  {
    q: "How can parents apply?",
    a: "Parents can complete the online admission form or visit the school admission office.",
  },
];

export default function GlobalMontessori() {
  return (
    <div className="gmp-root">
    <SchoolHero
        title="GlobalMontessori"
        subtitle="World-Class Infrastructure"
         image="./images/high.jpg"
      />
      <ProgrammeIntro />

      <WhatIsMontessori />

      <LearningPhilosophy />

      <AgeCriteria />

      <LearningAreas />

      <WhyChoose />

      <FeeStructure />

      <Subjects />

      <FAQ />

      <AdmissionCTA />
    </div>
  );
}

function ProgrammeIntro() {
  return (
    <section className="gmp-section programme-intro">
      <div className="gmp-container">
        <div className="intro-grid">
          <div className="intro-image">
            <img src={aboutImage} alt="Children Learning" />
          </div>

          <div className="intro-content">
            <span className="section-tag">About Our Programme</span>

            <h2>Building Strong Foundations Through Montessori Education</h2>

            <p>
              Our Montessori programme combines internationally recognised
              teaching practices with Nepal's educational standards, helping
              children become independent thinkers, confident communicators and
              lifelong learners.
            </p>

            <p>
              Every classroom provides a nurturing environment where children
              explore, create, collaborate and discover through meaningful
              activities designed around their developmental needs.
            </p>

            <div className="intro-features">
              <div className="intro-feature">✅ Child-Centred Learning</div>

              <div className="intro-feature">✅ Activity Based Curriculum</div>

              <div className="intro-feature">✅ Safe & Caring Environment</div>

              <div className="intro-feature">✅ Individual Attention</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function WhatIsMontessori() {
  return (
    <section className="gmp-section what-montessori">
      <div className="gmp-container">
        <div className="montessori-grid">
          {}

          <div className="montessori-content">
            <span className="section-tag">What is Montessori?</span>

            <h2>Learning Through Exploration & Discovery</h2>

            <p>
              Montessori education is a globally recognised approach developed
              by Dr. Maria Montessori. Instead of memorising lessons, children
              learn through exploration, observation, experimentation and
              practical experiences.
            </p>

            <p>
              Every child develops at a unique pace. Montessori classrooms
              encourage independent thinking, creativity and confidence by
              allowing children to choose meaningful activities while teachers
              act as caring guides throughout the learning process.
            </p>

            <p>
              At <strong>{SCHOOL.name}</strong>, Montessori principles are
              thoughtfully blended with Nepal's curriculum to prepare confident,
              responsible and future-ready learners.
            </p>

            <div className="montessori-points">
              <div className="point">✅ Child-led learning</div>

              <div className="point">✅ Hands-on experiences</div>

              <div className="point">✅ Individual learning pace</div>

              <div className="point">✅ Real-life practical skills</div>
            </div>

            <Link to={LINKS.admission} className="gmp-btn-primary">
              Apply For Admission
            </Link>
          </div>

          {}

          <div className="montessori-image">
            <img src="./images/s.jpeg" alt="Montessori Classroom" />
          </div>
        </div>
      </div>
    </section>
  );
}

function LearningPhilosophy() {
  return (
    <section className="gmp-section philosophy">
      <div className="gmp-container">
        <div className="philosophy-header">
          <span className="section-tag">Our Learning Philosophy</span>

          <h2>Helping Every Child Reach Their Full Potential</h2>

          <p>
            Our philosophy is built on curiosity, confidence, collaboration and
            creativity. We believe children learn best when they are actively
            engaged, encouraged to ask questions and supported in discovering
            solutions on their own.
          </p>
        </div>

        <div className="philosophy-main">
          {}

          <div className="philosophy-image">
            <img src={philosophyImage} alt="Learning Philosophy" />
          </div>

          {}

          <div className="philosophy-grid">
            <div className="philosophy-card">
              <div className="philosophy-icon">🌱</div>

              <h3>Explore</h3>

              <p>
                Children investigate, observe and discover new ideas through
                meaningful activities and practical learning.
              </p>
            </div>

            <div className="philosophy-card">
              <div className="philosophy-icon">🎨</div>

              <h3>Create</h3>

              <p>
                Art, music, storytelling and imaginative projects help children
                express themselves with confidence.
              </p>
            </div>

            <div className="philosophy-card">
              <div className="philosophy-icon">🤝</div>

              <h3>Collaborate</h3>

              <p>
                Teamwork, communication and respect encourage children to grow
                together in a positive learning environment.
              </p>
            </div>

            <div className="philosophy-card">
              <div className="philosophy-icon">🚀</div>

              <h3>Grow</h3>

              <p>
                Every learner develops independence, confidence and
                responsibility while preparing for future academic success.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function AgeCriteria() {
  return (
    <section className="gmp-section age-section">
      <div className="gmp-container">
        <div className="section-heading">
          <span className="section-tag">Admission Age Criteria</span>

          <h2>Find The Perfect Beginning For Your Child</h2>

          <p>
            Every child grows at their own pace. Our Montessori programme is
            carefully structured according to age and developmental milestones,
            ensuring every learner receives the right guidance at the right
            stage.
          </p>
        </div>

        <div className="age-grid">
          {AGE_GROUPS.map((item, index) => (
            <div className="age-card" key={item.title}>
              <div className="age-count">0{index + 1}</div>

              <h3>{item.title}</h3>

              <span className="age-value">{item.age}</span>

              <p>{item.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function LearningAreas() {
  return (
    <section className="gmp-section learning-section">
      <div className="gmp-container">
        <div className="section-heading">
          <span className="section-tag">Learning Areas</span>

          <h2>A Complete Montessori Curriculum</h2>

          <p>
            Our curriculum nurtures intellectual, emotional, physical and
            creative development through carefully designed Montessori learning
            experiences.
          </p>
        </div>

        <div className="learning-grid">
          {LEARNING_AREAS.map((item) => (
            <div className="learning-card" key={item.title}>
              <div className="learning-icon">{item.icon}</div>

              <h3>{item.title}</h3>

              <p>{item.description}</p>
            </div>
          ))}
        </div>

        <div className="learning-bottom">
          <div className="learning-image">
            <img src="./images/gg.jpeg" alt="Montessori Activities" />
          </div>

          <div className="learning-content">
            <h3>Learning Beyond The Classroom</h3>

            <p>
              Children actively participate in storytelling, gardening, music,
              dance, art, science activities, outdoor exploration and
              collaborative projects that encourage curiosity and independent
              thinking.
            </p>

            <ul>
              <li>✔ Practical Life Activities</li>

              <li>✔ Interactive Story Sessions</li>

              <li>✔ Nature Exploration</li>

              <li>✔ Creative Art & Craft</li>

              <li>✔ Music & Movement</li>

              <li>✔ STEM Based Activities</li>
            </ul>

            <Link to={LINKS.enquiry} className="gmp-btn-primary">
              Visit Our Campus
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

function WhyChoose() {
  return (
    <section className="gmp-section why-section">
      <div className="gmp-container">
        <div className="why-grid">
          {}

          <div className="why-image">
            <img src="kk.jpeg" alt="Montessori Students" />
          </div>

          {}

          <div className="why-content">
            <span className="section-tag">Why Choose Sahara?</span>

            <h2>A Safe, Happy & Future Ready Learning Environment</h2>

            <p>
              Sahara Public Secondary School provides a nurturing Montessori
              environment where every child receives individual attention,
              practical experiences and opportunities to develop confidence,
              creativity and leadership from the earliest years.
            </p>

            <div className="why-list">
              {WHY_CHOOSE.map((item) => (
                <div className="why-item" key={item}>
                  <span>✔</span>

                  <p>{item}</p>
                </div>
              ))}
            </div>

            <Link to={LINKS.enquiry} className="gmp-btn-primary">
              Schedule A School Visit
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

function FeeStructure() {
  const [activeTab, setActiveTab] = useState("fee");

  return (
    <section className="gmp-section fee-section">
      <div className="gmp-container">
        <div className="section-heading">
          <span className="section-tag">Admissions</span>

          <h2>Fee Structure & Admission Requirements</h2>

          <p>
            We believe quality education should remain accessible to every
            family. For the latest fee structure, scholarships, transport
            charges and payment options, please contact the admission office.
          </p>
        </div>

        <div className="fee-tabs">
          <button
            className={activeTab === "fee" ? "active" : ""}
            onClick={() => setActiveTab("fee")}
          >
            Fee Structure
          </button>

          <button
            className={activeTab === "documents" ? "active" : ""}
            onClick={() => setActiveTab("documents")}
          >
            Required Documents
          </button>
        </div>

        {activeTab === "fee" ? (
          <div className="fee-table">
            <div className="fee-row fee-head">
              <span>Description</span>

              <span>Amount (NPR)</span>
            </div>

            <div className="fee-row">
              <span>Admission Fee</span>

              <span>Contact School Office</span>
            </div>

            <div className="fee-row">
              <span>Annual Development Fee</span>

              <span>Contact School Office</span>
            </div>

            <div className="fee-row">
              <span>Monthly Tuition Fee</span>

              <span>Contact School Office</span>
            </div>

            <div className="fee-row">
              <span>Transport Fee (Optional)</span>

              <span>As Per Route</span>
            </div>

            <div className="fee-row">
              <span>Learning Materials</span>

              <span>Included / As Applicable</span>
            </div>
          </div>
        ) : (
          <div className="document-grid">
            <div className="document-card">📄 Birth Certificate</div>

            <div className="document-card">📷 Passport Size Photographs</div>

            <div className="document-card">🪪 Parent Citizenship Copy</div>

            <div className="document-card">📘 Previous School Report Card</div>

            <div className="document-card">
              📑 Transfer Certificate (If Applicable)
            </div>

            <div className="document-card">💉 Vaccination Record</div>
          </div>
        )}
      </div>
    </section>
  );
}

function Subjects() {
  return (
    <section className="gmp-section subjects-section">
      <div className="gmp-container">
        <div className="section-heading">
          <span className="section-tag">Montessori Curriculum</span>

          <h2>Learning Areas</h2>

          <p>
            Our early years curriculum blends academics, creativity, movement
            and practical experiences to develop confident, curious and
            independent learners.
          </p>
        </div>

        <div className="subjects-grid">
          <div className="subject-card">📖 English Language</div>

          <div className="subject-card">🇳🇵 Nepali Language</div>

          <div className="subject-card">🔢 Mathematics</div>

          <div className="subject-card">🌍 Environmental Studies</div>

          <div className="subject-card">🎨 Art & Craft</div>

          <div className="subject-card">🎵 Music & Dance</div>

          <div className="subject-card">🤸 Physical Activities</div>

          <div className="subject-card">🧩 Montessori Materials</div>

          <div className="subject-card">🌱 Nature Exploration</div>

          <div className="subject-card">💻 Digital Learning</div>

          <div className="subject-card">🧪 Science Discovery</div>

          <div className="subject-card">🗣 Communication Skills</div>
        </div>
      </div>
    </section>
  );
}

function FAQ() {
  const [open, setOpen] = useState(0);

  return (
    <section className="gmp-section faq-section">
      <div className="gmp-container">
        <div className="section-heading">
          <span className="section-tag">FAQs</span>

          <h2>Frequently Asked Questions</h2>
        </div>

        <div className="faq-list">
          {FAQS.map((faq, index) => (
            <div
              className={`faq-item ${open === index ? "active" : ""}`}
              key={faq.q}
            >
              <button
                className="faq-question"
                onClick={() => setOpen(open === index ? -1 : index)}
              >
                {faq.q}

                <span>{open === index ? "−" : "+"}</span>
              </button>

              {open === index && (
                <div className="faq-answer">
                  <p>{faq.a}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function AdmissionCTA() {
  return (
    <section className="gmp-admission-cta">
      <div className="gmp-container">
        <div className="cta-content">
          <span className="section-tag">Admissions Open</span>

          <h2>Give Your Child The Best Start In Life</h2>

          <p>
            Join Sahara Public Secondary School's Montessori Programme and
            provide your child with a joyful, safe and inspiring learning
            environment that nurtures confidence, creativity, curiosity and
            lifelong success.
          </p>

          <div className="cta-buttons">
            <Link to={LINKS.admission} className="gmp-btn-primary">
              Apply Now
            </Link>

            <Link to={LINKS.enquiry} className="gmp-btn-outline">
              Contact Admissions
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}
